<script setup lang="ts">
// Mobile navigation drawer (below lg). Mirrors the desktop category bar and
// the account menu so nothing is reachable only on desktop. Opened from the
// burger in AppTopbar; closes on backdrop click, Escape (handled in App.vue)
// and route change.
import { computed, nextTick, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { LayoutGrid, X } from 'lucide-vue-next';
import { useAuthStore } from '../stores/auth';
import { PLATFORMS, platformLink } from '../data/platforms';
import BrandLogo from './BrandLogo.vue';
import LanguageSwitcher from './LanguageSwitcher.vue';

const { t } = useI18n();
const auth = useAuthStore();

const props = defineProps<{ open?: boolean }>();
const emit = defineEmits<{ close: [] }>();

// Move focus into the drawer when it opens so keyboard users land on the
// close button instead of staying on the (now covered) page.
const closeBtn = ref<HTMLButtonElement | null>(null);
watch(
  () => props.open,
  async (open) => {
    if (!open) return;
    await nextTick();
    closeBtn.value?.focus();
  },
);

const shortcuts = computed(() => [
  { to: '/catalog?sort=price_asc', label: t('nav.deals') },
  { to: '/catalog?sort=newest', label: t('nav.newReleases') },
  { to: '/articles', label: t('nav.articles') },
  { to: '/help', label: t('nav.help') },
]);

const account = computed(() => [
  { to: '/account', label: t('nav.account') },
  { to: '/orders', label: t('nav.orders') },
  { to: '/favorites', label: t('nav.favourites') },
  { to: '/account/billing', label: t('nav.billingWallet') },
  { to: '/account/settings', label: t('nav.settings') },
]);

const item = 'flex h-11 items-center gap-3 rounded-lg px-3 text-[15px] font-medium text-text transition-colors hover:bg-card';
</script>

<template>
  <Transition
    enter-active-class="transition-opacity duration-200 motion-reduce:transition-none"
    leave-active-class="transition-opacity duration-150 motion-reduce:transition-none"
    enter-from-class="opacity-0"
    leave-to-class="opacity-0"
  >
    <div v-if="open" aria-hidden="true" class="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm lg:hidden" @click="emit('close')"></div>
  </Transition>

  <Transition
    enter-active-class="transition-transform duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
    leave-active-class="transition-transform duration-200 ease-in motion-reduce:transition-none"
    enter-from-class="-translate-x-full"
    leave-to-class="-translate-x-full"
  >
    <aside
      v-if="open"
      id="mobile-nav"
      role="dialog"
      aria-modal="true"
      class="bg-glass fixed inset-y-0 left-0 z-50 flex w-[300px] max-w-[85vw] flex-col overflow-y-auto border-r border-white/10 lg:hidden"
      :aria-label="t('nav.menu')"
    >
      <div class="flex h-[68px] items-center justify-between px-4">
        <RouterLink to="/" class="flex items-center" @click="emit('close')"><BrandLogo size="sm" /></RouterLink>
        <button ref="closeBtn" type="button" :aria-label="t('nav.closeMenu')" class="grid size-10 place-items-center rounded-lg text-text-dim hover:bg-card hover:text-text" @click="emit('close')">
          <X class="size-5" :stroke-width="1.75" />
        </button>
      </div>

      <nav class="flex flex-col gap-6 px-3 pb-6" :aria-label="t('nav.browse')">
        <section>
          <RouterLink to="/catalog" :class="item" @click="emit('close')">
            <LayoutGrid class="size-5 text-text-dim" :stroke-width="1.75" />
            {{ t('nav.allGames') }}
          </RouterLink>
          <ul class="mt-1 grid grid-cols-2 gap-1">
            <li v-for="p in PLATFORMS" :key="p.name">
              <RouterLink :to="platformLink(p.facets)" class="flex h-10 items-center gap-2.5 rounded-lg px-3 text-[14px] text-text-dim hover:bg-card hover:text-text" @click="emit('close')">
                <img :src="p.icon" alt="" aria-hidden="true" class="size-4 brightness-0 invert opacity-80" />
                {{ p.name }}
              </RouterLink>
            </li>
          </ul>
        </section>

        <section class="border-t border-border-dim pt-4">
          <ul class="flex flex-col gap-0.5">
            <li v-for="l in shortcuts" :key="l.to"><RouterLink :to="l.to" :class="item" @click="emit('close')">{{ l.label }}</RouterLink></li>
          </ul>
        </section>

        <section class="border-t border-border-dim pt-4">
          <template v-if="auth.isAuthenticated">
            <p class="px-3 pb-2 text-[12px] font-semibold text-text-mute">{{ auth.user?.email }}</p>
            <ul class="flex flex-col gap-0.5">
              <li v-for="l in account" :key="l.to"><RouterLink :to="l.to" :class="item" @click="emit('close')">{{ l.label }}</RouterLink></li>
            </ul>
          </template>
          <div v-else class="grid grid-cols-2 gap-2 px-1">
            <RouterLink to="/login" class="flex h-11 items-center justify-center rounded-lg border border-border text-[14px] font-medium text-text hover:bg-card" @click="emit('close')">{{ t('nav.logIn') }}</RouterLink>
            <RouterLink to="/register" class="flex h-11 items-center justify-center rounded-lg bg-brand-gradient font-display text-[14px] font-semibold text-white" @click="emit('close')">{{ t('nav.signUp') }}</RouterLink>
          </div>
        </section>
      </nav>

      <div class="mt-auto flex items-center justify-between border-t border-border-dim px-4 py-3">
        <LanguageSwitcher placement="up" />
      </div>
    </aside>
  </Transition>
</template>
